import {
  ComponentRef,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import {
  Field,
  FieldProps,
  FormikErrors,
  FormikProvider,
  useFormik,
} from "formik";
import { Flex, Form, Input, Switch } from "antd";
import { BannerUpload, LexicalEditor, Select } from "ui";
import { FieldError } from "ui/field-error";
import { getRand } from "core/utils/common-utils";
import { getImageSrc } from "core/utils/json-utils";
import { useGetArticleCatsFlat } from "pages/articles/api";
import { useListAuthors } from "pages/authors/api";
import { articleForm } from "pages/articles/type";
import {
  addArticleFormSchema,
  editArticleFormSchema,
} from "pages/articles/schema";

interface ArticleBaseFormProps {
  formData?: articleForm;
  isEdit?: boolean;
}
interface RefHandler {
  getFormData: () => {
    values: articleForm;
    validation: Promise<FormikErrors<articleForm>>;
  };
  resetForm: () => void;
}
export type ArticleFormRef = ComponentRef<typeof ArticleBaseForm>;

const ArticleBaseForm = forwardRef<RefHandler, ArticleBaseFormProps>(
  ({ formData, isEdit = true }, ref) => {
    const labelsCol = isEdit ? 3 : 5;

    const [refreshKey, setRefreshKey] = useState("");

    const { data: authorsData, isPending: fetchingAuthors } = useListAuthors({
      page: 1,
      pageSize: 1000,
      inputParams: {
        filters: [],
        sort: {
          propertyName: "id",
          ascending: false,
        },
      },
    });
    const authorsOptions = useMemo(() => {
      if (authorsData) {
        return (authorsData?.data?.data ?? []).map((item) => ({
          label: item.fullName,
          value: item.id,
        }));
      }
      return [];
    }, [authorsData]);

    const { data: articleCatsData, isPending: fetchingArticleCats } =
      useGetArticleCatsFlat();
    const articleCatsOptions = useMemo(() => {
      if (articleCatsData) {
        return (articleCatsData?.data ?? []).map((item) => ({
          label: item.name,
          value: item.id,
        }));
      }
      return [];
    }, [articleCatsData]);

    const [form] = Form.useForm();
    const formik = useFormik<Partial<articleForm>>({
      initialValues: {
        id: formData?.id,
        title: formData?.title,
        subTitle: formData?.subTitle ?? "",
        summary: formData?.summary ?? "",
        content: formData?.content ?? "",
        timeToReadInMinutes: formData?.timeToReadInMinutes
          ? Number(formData.timeToReadInMinutes)
          : 0,
        showComment: formData?.showComment ?? false,
        showLastArticles: formData?.showLastArticles ?? false,
        showRelatedProduct: formData?.showRelatedProduct ?? false,
        authorId: formData?.authorId,
        articleCategoryId: formData?.articleCategoryId,
        thumbnail: formData?.thumbnail,
        image: formData?.image,
        uploadImage: undefined,
        state: formData?.state,
        altText: formData?.image?.altText,
      },
      validationSchema: isEdit ? editArticleFormSchema : addArticleFormSchema,
      validateOnBlur: false,
      validateOnMount: false,
      validateOnChange: false,
      enableReinitialize: true,
      onSubmit: () => { },
    });
    const { setFieldValue, values, errors, validateForm, resetForm } = formik;

    useImperativeHandle(ref, () => ({
      getFormData() {
        return {
          validation: validateForm(),
          values: values as articleForm,
        };
      },
      resetForm() {
        resetForm();
        setRefreshKey(`${getRand()}`);
        form.resetFields();
      },
    }));

    useEffect(() => {
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 50)
    }, [formData]);

    return (
      <FormikProvider value={formik}>
        <Form form={form}>
          <Form.Item
            label="تصویر اصلی"
            labelCol={{ span: isEdit ? labelsCol : 4 }}
            labelAlign="left"
            name="uploadImage"
            valuePropName="uploadImage"
          >
            <BannerUpload
              key={`article-${refreshKey}`}
              value={
                values.thumbnail?.url
                  ? {
                    width: values.thumbnail!.width,
                    height: values.thumbnail!.width,
                    preview: getImageSrc(values.thumbnail?.url, true),
                  }
                  : undefined
              }
              name="uploadImage"
              title="تصویر پیش نمایش مقاله"
              listType="picture-card"
              className="avatar-uploader"
              onBannerChange={(banner) => {
                setFieldValue("thumbnail", undefined);
                setFieldValue("image", undefined);
                setFieldValue("uploadImage", banner);
              }}
            />
          </Form.Item>
          <Form.Item
            label="آلت تصویر اصلی"
            labelCol={{ span: labelsCol }}
            labelAlign="left"
            name="altText"
          >
            <Field name="altText">
              {({ field }: FieldProps) => (
                <Input
                  {...field}
                  placeholder="لطفا آلت تصویر اصلی را وارد کنید"
                  value={values.altText}
                  onChange={(e) => setFieldValue("altText", e.target.value)}
                />
              )}
            </Field>
          </Form.Item>
          <Form.Item
            label="موضوع مقاله"
            labelCol={{ span: labelsCol }}
            labelAlign="left"
            name="title"
            rules={[
              {
                required: true,
                message: "لطفا نام مقاله را وارد کنید",
              },
            ]}
          >
            <Field name="title">
              {({ field }: FieldProps) => (
                <Input
                  {...field}
                  placeholder="لطفا تایپ کنید"
                  value={values.title}
                  onChange={(e) => setFieldValue("title", e.target.value)}
                />
              )}
            </Field>
            <FieldError show={errors.title} text={errors.title} />
          </Form.Item>
          {isEdit && (
            <>
              <Form.Item
                label="زیر عنوان"
                labelCol={{ span: labelsCol }}
                labelAlign="left"
                name="subTitle"
                rules={[
                  {
                    required: true,
                    message: "لطفا زیر عنوان مقاله را وارد کنید",
                  },
                ]}
              >
                <Field name="subTitle">
                  {({ field }: FieldProps) => (
                    <Input
                      {...field}
                      placeholder="لطفا تایپ کنید"
                      value={values.subTitle}
                      onChange={(e) =>
                        setFieldValue("subTitle", e.target.value)
                      }
                    />
                  )}
                </Field>
              </Form.Item>
              <Form.Item
                label="خلاصه"
                labelCol={{ span: labelsCol }}
                labelAlign="left"
                name="summary"
                rules={[
                  {
                    required: true,
                    message: "لطفا خلاصه مقاله را وارد کنید",
                  },
                ]}
              >
                <Field name="summary">
                  {({ field }: FieldProps) => (
                    <Input
                      {...field}
                      placeholder="لطفا تایپ کنید"
                      value={values.summary}
                      onChange={(e) => setFieldValue("summary", e.target.value)}
                    />
                  )}
                </Field>
              </Form.Item>
              <Form.Item
                label="زمان مطالعه"
                labelCol={{ span: labelsCol }}
                labelAlign="left"
                name="timeToReadInMinutes"
                rules={[
                  {
                    required: true,
                    message: "لطفا زمان مطالعه ی مقاله را وارد کنید",
                  },
                ]}
              >
                <Field name="timeToReadInMinutes">
                  {({ field }: FieldProps) => (
                    <Input
                      {...field}
                      placeholder="زمان مطالعه را به دقیقه وارد کنید"
                      type="number"
                      min="0"
                      value={values.timeToReadInMinutes}
                      onChange={(e) =>
                        setFieldValue("timeToReadInMinutes", e.target.value)
                      }
                    />
                  )}
                </Field>
              </Form.Item>
            </>
          )}
          <Form.Item
            label="دسته بندی مقاله"
            labelCol={{ span: labelsCol }}
            labelAlign="left"
            name="articleCategoryId"
          >
            <Field name="articleCategoryId">
              {({ field }: FieldProps) => (
                <Select
                  {...field}
                  placeholder="دسته بندی مقاله را انتخاب کنید  "
                  onChange={(e) => setFieldValue("articleCategoryId", e)}
                  value={values.articleCategoryId}
                  options={articleCatsOptions}
                  loading={fetchingArticleCats}
                />
              )}
            </Field>
            <FieldError
              show={errors.articleCategoryId}
              text={errors.articleCategoryId}
            />
          </Form.Item>
          <Form.Item
            label="نویسنده"
            labelCol={{ span: labelsCol }}
            labelAlign="left"
            name="authorId"
          >
            <Field name="authorId">
              {({ field }: FieldProps) => (
                <Select
                  {...field}
                  placeholder="نویسنده مقاله را انتخاب کنید  "
                  onChange={(e) => setFieldValue("authorId", e)}
                  value={values.authorId}
                  options={authorsOptions}
                  loading={fetchingAuthors}
                />
              )}
            </Field>
            <FieldError show={errors.authorId} text={errors.authorId} />
          </Form.Item>
          <Flex gap="10">
            <Form.Item
              name={"showComment"}
              label="نمایش نظرات"
              colon={false}
              labelCol={{ span: 9 }}
              labelAlign="left"
              className="w-100"
            >
              <Field name="showComment">
                {({ field }: FieldProps) => (
                  <Switch
                    {...field}
                    checked={values.showComment}
                    onChange={(checked) =>
                      setFieldValue("showComment", checked)
                    }
                  />
                )}
              </Field>
            </Form.Item>
            <Form.Item
              name={"showRelatedProduct"}
              label={<label className="text-wrap">نمایش محصولات مرتبط</label>}
              colon={false}
              labelCol={{ span: 12 }}
              labelAlign="left"
              className="w-100"
            >
              <Field name="showRelatedProduct">
                {({ field }: FieldProps) => (
                  <Switch
                    {...field}
                    checked={values.showRelatedProduct}
                    onChange={(checked) =>
                      setFieldValue("showRelatedProduct", checked)
                    }
                  />
                )}
              </Field>
            </Form.Item>
            <Form.Item
              name="showLastArticles"
              label={<label className="text-wrap">نمایش آخرین مطالب</label>}
              colon={false}
              labelCol={{ span: 12 }}
              labelAlign="left"
              className="w-100"
            >
              <Field name="showLastArticles">
                {({ field }: FieldProps) => (
                  <Switch
                    {...field}
                    checked={values.showLastArticles}
                    onChange={(checked) =>
                      setFieldValue("showLastArticles", checked)
                    }
                  />
                )}
              </Field>
            </Form.Item>
            <Form.Item
              name="state"
              label={<label className="text-wrap">انتشار</label>}
              colon={false}
              labelCol={{ span: 10 }}
              labelAlign="left"
              className="w-50"
            >
              <Field name="state">
                {({ field }: FieldProps) => (
                  <Switch
                    {...field}
                    checked={Number(values.state) === 1}
                    onChange={(checked) =>
                      setFieldValue("state", checked ? 1 : 0)
                    }
                  />
                )}
              </Field>
            </Form.Item>
          </Flex>
          {isEdit && (
            <Form.Item
              label="محتوا"
              labelCol={{ span: 2 }}
              labelAlign="left"
              name="content"
              rules={[
                {
                  required: true,
                  message: "لطفا محتوای مقاله را وارد کنید",
                },
              ]}
            >
              <Field name="content">
                {({ field }: FieldProps) => (
                  <LexicalEditor
                    {...field}
                    placeholder="لطفا تایپ کنید"
                    onChange={(e: string) => setFieldValue("content", e)}
                    value={formData?.content}
                  />
                )}
              </Field>
            </Form.Item>
          )}
        </Form>
      </FormikProvider>
    );
  }
);

export default ArticleBaseForm;
